import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';

import type { Money } from '@nucleus-commerce/api-client/admin';
import { moneyToDisplayNumber } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@nucleus-commerce/ui/components/chart';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface SalesPoint {
  label: string;
  gross: Money;
  ordersCount: number;
}

export interface SalesChartProps {
  points: readonly SalesPoint[];
  currency: string;
  /** Read by screen readers in place of the chart. */
  summary: string;
  className?: string;
}

/** Gross as bars on the left axis, orders as a line on the right one. */
export function SalesChart({ points, currency, summary, className }: SalesChartProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();

  const config = useMemo(
    () =>
      ({
        gross: { label: t('chart.gross'), color: 'var(--chart-1)' },
        orders: { label: t('chart.orders'), color: 'var(--chart-2)' },
      }) satisfies ChartConfig,
    [t],
  );

  const data = useMemo(
    () =>
      points.map((p) => ({
        label: p.label,
        gross: moneyToDisplayNumber(p.gross),
        orders: p.ordersCount,
      })),
    [points],
  );

  const compactMoney = (value: number) =>
    new Intl.NumberFormat(format.locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);

  return (
    <figure className={cn('m-0', className)}>
      <figcaption className="sr-only">{summary}</figcaption>
      <ChartContainer config={config} className="aspect-auto h-[280px] w-full" aria-hidden>
        <ComposedChart data={data} margin={{ left: 4, right: 4, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={16} />
          <YAxis
            yAxisId="gross"
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={compactMoney}
          />
          <YAxis
            yAxisId="orders"
            orientation="right"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={32}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, name) => (
                  <div className="flex w-full items-center justify-between gap-3">
                    <span className="text-muted-foreground">
                      {name === 'gross' ? t('chart.gross') : t('chart.orders')}
                    </span>
                    <span className="font-mono font-medium tabular-nums">
                      {name === 'gross'
                        ? format.number(Number(value), { style: 'currency', currency })
                        : format.number(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar yAxisId="gross" dataKey="gross" fill="var(--color-gross)" radius={[3, 3, 0, 0]} />
          <Line
            yAxisId="orders"
            dataKey="orders"
            type="monotone"
            stroke="var(--color-orders)"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ChartContainer>
    </figure>
  );
}
